import { useId, useState } from 'react';
import { Archive, ArrowDown, ArrowUp, Check, ListOrdered, Pencil, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  EmptyState,
  IconButton,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@/components/ui';
import {
  moveDictionary,
  validateDictionary,
  type DictionaryEntry,
  type DictionaryTone,
  type RelationState,
} from './relationData';
const tones: { id: DictionaryTone; label: string }[] = [
  { id: 'neutral', label: 'Нейтральный' },
  { id: 'info', label: 'Информация' },
  { id: 'success', label: 'Успех' },
  { id: 'warn', label: 'Внимание' },
];
export function DictionaryEditor({
  entries,
  onChange,
  createId,
  readOnly = false,
  state = 'ready',
  onRetry,
}: {
  entries: readonly DictionaryEntry[];
  onChange?: (entries: DictionaryEntry[]) => void | Promise<void>;
  createId?: () => string;
  readOnly?: boolean;
  state?: RelationState;
  onRetry?: () => void;
}) {
  const id = useId(),
    [dialog, setDialog] = useState<{
      id: string | null;
      label: string;
      tone: DictionaryTone;
    } | null>(null),
    [pending, setPending] = useState(false),
    [error, setError] = useState('');
  const invalid = validateDictionary(entries),
    canEdit = !readOnly && !!onChange;
  async function commit(next: DictionaryEntry[], close = false) {
    const issue = validateDictionary(next);
    if (issue) {
      setError(issue);
      return;
    }
    if (!onChange || readOnly || state !== 'ready') return;
    setError('');
    setPending(true);
    try {
      await onChange(next);
      if (close) setDialog(null);
    } catch {
      setError('Справочник не сохранён. Изменения в форме остались.');
    } finally {
      setPending(false);
    }
  }
  function save() {
    if (!dialog) return;
    const entry: DictionaryEntry = {
      id: dialog.id ?? createId?.() ?? '',
      label: dialog.label.trim(),
      tone: dialog.tone,
    };
    if (dialog.id) {
      const old = entries.find((item) => item.id === dialog.id);
      if (!old) {
        setError('Значение уже изменилось. Закрой окно и открой его снова.');
        return;
      }
      entry.archived = old.archived;
    }
    void commit(
      dialog.id ? entries.map((item) => (item.id === dialog.id ? entry : item)) : [...entries, entry],
      true,
    );
  }
  return (
    <section data-slot="dictionary-editor" aria-labelledby={id} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id={id} className="type-heading">
            Справочник значений
          </h3>
          <p className="mt-1 text-xs text-fg-muted">
            Название и цвет меняются; идентификатор остаётся. Порядок — кнопками со стрелками.
          </p>
        </div>
        {canEdit && createId && (
          <Button
            variant="secondary"
            size="sm"
            disabled={pending || state !== 'ready' || !!invalid}
            onClick={() => {
              setError('');
              setDialog({ id: null, label: '', tone: 'neutral' });
            }}
          >
            <Plus className="size-4" />
            Добавить значение
          </Button>
        )}
      </div>
      {state === 'loading' ? (
        <div aria-label="Загрузка справочника" className="space-y-2">
          {[0, 1, 2].map((n) => (
            <Skeleton key={n} className="h-14 w-full" />
          ))}
        </div>
      ) : state === 'error' || invalid ? (
        <EmptyState
          title="Справочник недоступен"
          icon={<ListOrdered />}
          description={invalid ?? 'Не удалось загрузить значения.'}
          action={onRetry ? { label: 'Повторить', onClick: onRetry } : undefined}
        />
      ) : !entries.length ? (
        <EmptyState
          title="Справочник пуст"
          icon={<ListOrdered />}
          description="Добавь первое значение для статусов или категорий."
        />
      ) : (
        <ol className="divide-y divide-line rounded-lg border border-line">
          {entries.map((item, index) => (
            <li key={item.id} data-entry-id={item.id} className="flex flex-wrap items-center gap-3 p-3">
              <span className="w-5 shrink-0 text-right text-xs text-fg-subtle tabular-nums">{index + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={item.tone} solid>
                    {item.label}
                  </Badge>
                  {item.archived && <Badge>В архиве</Badge>}
                </div>
                <p className="mt-1 font-mono text-2xs break-all text-fg-subtle">{item.id}</p>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1">
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={'Выше: ' + item.label}
                    disabled={pending || index === 0}
                    onClick={() => void commit(moveDictionary(entries, item.id, -1))}
                  >
                    <ArrowUp />
                  </IconButton>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={'Ниже: ' + item.label}
                    disabled={pending || index === entries.length - 1}
                    onClick={() => void commit(moveDictionary(entries, item.id, 1))}
                  >
                    <ArrowDown />
                  </IconButton>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={'Изменить: ' + item.label}
                    disabled={pending}
                    onClick={() => {
                      setError('');
                      setDialog({
                        id: item.id,
                        label: item.label,
                        tone: item.tone,
                      });
                    }}
                  >
                    <Pencil />
                  </IconButton>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={(item.archived ? 'Вернуть из архива: ' : 'В архив: ') + item.label}
                    disabled={pending}
                    onClick={() =>
                      void commit(
                        entries.map((value) =>
                          value.id === item.id ? { ...value, archived: !value.archived } : value,
                        ),
                      )
                    }
                  >
                    {item.archived ? <Check /> : <Archive />}
                  </IconButton>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {error && !dialog && (
        <p role="alert" className="text-sm text-danger-strong">
          {error}
        </p>
      )}
      <Dialog
        open={!!dialog && canEdit}
        onOpenChange={(next) => {
          if (!next && !pending) setDialog(null);
        }}
      >
        <DialogContent>
          <DialogHeader
            title={dialog?.id ? 'Изменить значение' : 'Новое значение'}
            description="Архивное значение остаётся в старых объектах, но больше не предлагается при выборе."
          />
          {dialog && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                save();
              }}
              className="space-y-4 px-6 pb-6"
            >
              <div className="space-y-1.5">
                <label htmlFor={id + '-name'} className="text-xs font-medium text-fg-muted">
                  Название
                </label>
                <Input
                  id={id + '-name'}
                  autoFocus
                  value={dialog.label}
                  onChange={(event) => setDialog({ ...dialog, label: event.target.value })}
                  maxLength={80}
                  disabled={pending}
                  aria-invalid={!!error}
                />
              </div>
              <div className="space-y-1.5">
                <label id={id + '-tone'} className="text-xs font-medium text-fg-muted">
                  Смысловой цвет
                </label>
                <Select
                  value={dialog.tone}
                  onValueChange={(tone) => setDialog({ ...dialog, tone: tone as DictionaryTone })}
                  disabled={pending}
                >
                  <SelectTrigger aria-labelledby={id + '-tone'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tones.map((tone) => (
                      <SelectItem key={tone.id} value={tone.id}>
                        {tone.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {error && (
                <p role="alert" className="text-sm text-danger-strong">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" disabled={pending} onClick={() => setDialog(null)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={pending}>
                  {pending ? 'Сохранение…' : 'Сохранить значение'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
